import { ButtonDropdown, Flex, Tag, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { quoteStatusToAllowedTransitions } from '@features/invoices/constants/quoteStatusToAllowedTransitions'
import { MarkQuoteAsAcceptedConfirmationModal } from '@features/invoices/elements/MarkQuoteAsAcceptedConfirmationModal'
import { QuoteInvoiceState } from '@features/invoices/enums/quoteInvoiceState'
import { QuoteStatus as QuoteStatusType } from '@features/invoices/enums/quoteStatus'
import { useUpdateQuoteStatus } from '@features/invoices/hooks/quotes/useUpdateQuoteStatus'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { useQuote } from '../../../../../../contexts/quoteContext'
import { QuoteStatusHeader } from './elements/QuoteStatusHeader'
import { getQuoteStatusLabel } from './utils/getQuoteStatusLabel'

export const QuoteStatus = (): ReactElement => {
  const { t } = useTranslation()
  const { quote, quoteId } = useQuote()
  const { updateQuoteStatus, isProcessing: isProcessingUpdateQuoteStatus } = useUpdateQuoteStatus()

  const { open: openConfirmationModal, close: closeConfirmationModal } = useModal(
    ModalId.MarkQuoteAsAcceptedConfirmationModal,
  )

  const status = quote?.status?.status ?? QuoteStatusType.Created
  const invoiceState = quote?.invoiceState

  const isAcceptedInvoice = status === QuoteStatusType.Accepted
  const isFullyInvoiced = invoiceState === QuoteInvoiceState.Invoiced
  const isPartiallyInvoiced = invoiceState === QuoteInvoiceState.PartiallyInvoiced

  const quoteStatusLabel = useMemo(() => {
    if (isPartiallyInvoiced) {
      return t('quotes.view.sidebar.details_card.status.partly_invoiced')
    }

    if (isFullyInvoiced) {
      return t('quotes.view.sidebar.details_card.status.invoiced')
    }

    return getQuoteStatusLabel(status, t)
  }, [isFullyInvoiced, isPartiallyInvoiced, status, t])

  const dropdownItems = useMemo(() => {
    const allowedQuoteStatusTransitions = quoteStatusToAllowedTransitions[status]
    return allowedQuoteStatusTransitions.map((nextStatus) => ({
      children: getQuoteStatusLabel(nextStatus, t),
      id: nextStatus,
      value: nextStatus,
    }))
  }, [status, t])

  const handleSelectNewStatus = useCallback(
    (_id: string, value: QuoteStatusType) => {
      if (value === QuoteStatusType.Accepted) {
        openConfirmationModal()
        return
      }
      updateQuoteStatus({ quoteId, value })
    },
    [quoteId, openConfirmationModal, updateQuoteStatus],
  )

  const handleConfirmAccepted = useCallback(() => {
    updateQuoteStatus({ quoteId, value: QuoteStatusType.Accepted })
    closeConfirmationModal()
  }, [quoteId, closeConfirmationModal, updateQuoteStatus])

  const statusLabel = useMemo(() => {
    const shouldShowInvoicedTag = isPartiallyInvoiced || isFullyInvoiced || status === QuoteStatusType.Invoiced
    if (shouldShowInvoicedTag) {
      return (
        <Tag type="subtle" color="blue" size="sm" icon={isPartiallyInvoiced ? 'circleDashed' : 'files'}>
          {quoteStatusLabel}
        </Tag>
      )
    }
    if (isAcceptedInvoice) {
      return (
        <Tag type="subtle" color="green" size="sm">
          {quoteStatusLabel}
        </Tag>
      )
    }

    return (
      <Text variant="bodyMD" weight="bold">
        {quoteStatusLabel}
      </Text>
    )
  }, [isAcceptedInvoice, isFullyInvoiced, isPartiallyInvoiced, quoteStatusLabel, status])

  const shouldDisplayStaticStatus =
    isPartiallyInvoiced ||
    isFullyInvoiced ||
    isAcceptedInvoice ||
    (!dropdownItems.length && !isProcessingUpdateQuoteStatus)

  if (shouldDisplayStaticStatus) {
    return <Flex>{statusLabel}</Flex>
  }

  return (
    <Flex>
      <ButtonDropdown
        dropdownHeader={<QuoteStatusHeader />}
        items={dropdownItems}
        loading={isProcessingUpdateQuoteStatus}
        onSelect={handleSelectNewStatus}
        placement="bottom"
        size="sm"
        variant="outlined"
      >
        {quoteStatusLabel}
      </ButtonDropdown>

      <MarkQuoteAsAcceptedConfirmationModal
        id={ModalId.MarkQuoteAsAcceptedConfirmationModal}
        onConfirm={handleConfirmAccepted}
      />
    </Flex>
  )
}
